<template>
  <v-container fluid v-if="ready">
    <v-alert text density="compact" type="error" v-if="missingSettings.length > 0">
      Fehlende Einstellungen:<br/>
      <v-chip size="small" v-for="missingSetting in missingSettings" :key="missingSetting">{{missingSetting}}</v-chip>
    </v-alert>
    <customer-select use-model use-filter show-label v-model="customerId" />
    <client-select
      use-model
      show-label
      :customer-id="customerId ?? undefined"
      v-model="client"
    />
    <subsidiary-select
      :client="client ?? undefined"
      :customer-id="customerId ?? undefined"
      use-model
      show-label
      v-model="selectedSubsidiary"
    />
    <warehouse-select
      :client="client ?? undefined"
      :customer-id="customerId ?? undefined"
      :subsidiary="selectedSubsidiary ?? undefined"
      use-model
      show-label
      v-model="selectedWarehouse"
    />
    <v-text-field
      hide-details
      class="mt-3 mb-2"
      v-model="storageBinManagementCmp"
      label="Lagerplatzverwaltung"
      :disabled="true"
    />
    <v-text-field
      hide-details
      class="mt-3 mb-5"
      v-model="userShortCmp"
      label="Bedienerkürzel"
      maxlength="5"
      :disabled="Boolean(settingsStore.sessionData?.Bediener.Kuerzel)"
      :rules="[validateUserShort]"
    />
    <v-btn color="primary" @click="save" :disabled="!changesMade">Speichern</v-btn>
    <v-snackbar v-model="showSnackbar" timeout="3000">
      Erfolgreich gespeichert
      <template v-slot:actions>
        <v-btn @click="showSnackbar = false">Schließen</v-btn>
      </template>
    </v-snackbar>
  </v-container>
  <div v-else />
</template>

<script setup lang="ts">
import CustomerSelect from '@/components/CustomerSelect.vue';
import WarehouseSelect from '@/components/WarehouseSelect.vue';
import SubsidiarySelect from '@/components/SubsidiarySelect.vue';
import ClientSelect from '@/components/ClientSelect.vue';
import {computed, ref, watch, watchEffect} from 'vue';
import {useApplicationState} from '@/store/applicationState';
import {useSettings} from '@/store/settings';
import {doAlert} from '@/util';

const emits = defineEmits(['save']);

const applicationState = useApplicationState();
const settingsStore = useSettings();
const missingSettings = computed((): string[] => {
  return applicationState.missingSettings;
});
const ready = computed((): boolean => {
  return settingsStore.ready;
});

settingsStore.fetchCustomers();

const client = ref(settingsStore.client);
const customerId = ref(settingsStore.customerId);
const userShort = ref(settingsStore.userShort);
const selectedSubsidiary = ref(settingsStore.selectedSubsidiary);
const selectedWarehouse = ref(settingsStore.selectedWarehouse);
const storageBinManagement = computed(() => settingsStore.sessionData?.Freischaltung.LagerplatzVerwaltung ?? false);

watch(customerId, () => {
  client.value = null;
});
const showSnackbar = ref(false);
const changesMade = ref(false);

watch(client, (newClient, oldClient) => {
  if (newClient === oldClient) return;

  if (newClient === null) {
    selectedSubsidiary.value = null;
    return;
  }

  selectedSubsidiary.value = settingsStore.selectedSubsidiary;
});

watch(selectedSubsidiary, async (newSubsidiary, oldSubsidiary) => {
  if (newSubsidiary === oldSubsidiary) return;

  if (client.value === null || newSubsidiary === null) {
    selectedWarehouse.value = null;
    return;
  }

  selectedWarehouse.value = settingsStore.selectedWarehouse;
});

watch(selectedWarehouse, (newWarehouse, oldWarehouse) => {
  if (newWarehouse === oldWarehouse) return;
});

watch(() => [client.value, selectedSubsidiary.value, selectedWarehouse.value, userShort.value], () => {
  changesMade.value = true;
});

const userShortCmp = computed<string>({
  get: () => settingsStore.sessionData?.Bediener.Kuerzel ?? userShort.value ?? '',
  set: (newVal: string) => userShort.value = newVal?.toUpperCase() ?? '',
});

const storageBinManagementCmp = computed<string>(() => storageBinManagement.value ? 'Aktiv' : 'Inaktiv');

watchEffect(() => {
  storageBinManagementCmp.value;
});

const save = async () => {
  try {
    settingsStore.customerId = customerId.value;
    settingsStore.client = client.value;
    settingsStore.selectedSubsidiary = selectedSubsidiary.value;
    settingsStore.selectedWarehouse = selectedWarehouse.value;
    settingsStore.userShort = userShort.value;
    emits('save');
    showSnackbar.value = true;
    changesMade.value = false;
  } catch (err: any) {
    await doAlert(err?.message, 'Fehler');
  }
};

function validateUserShort(val: string|null): string|true {
  if ((val?.length ?? 0) > 5) return 'zu lang';
  return true;
}

defineExpose({
  WarehouseSelect, SubsidiarySelect, ClientSelect, CustomerSelect,
  missingSettings,
  ready,
  userShort,
  selectedSubsidiary,
  selectedWarehouse,
  userShortCmp,
  storageBinManagementCmp,
  client,
  customerId,
  showSnackbar,
  changesMade,
  save,
  validateUserShort,
  settingsStore,
});
</script>
